import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import AdminIcon from "../../../assets/icons/AdminIcon.svg";
import AdminUsers from "./AdminUsers";
import InviteAdminModal from "./InviteAdminModal";
import EditAdminModal from "./EditAdminModal";
import RevokeAdminModal from "./RevokeAdminModal";
import AccountSettings from "./AccountSettings";
import PageHeader from "../../../shared/components/common/PageHeader";
import useAdminManagement from "../hooks/useAdminManagement";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { isAdmin, isViewer } from "../../../shared/utils/roles";

const AdminManagement = () => {
  const currentUser = useSelector(selectCurrentUser);
  const role = currentUser?.user_role;
  const admin = isAdmin(role);
  const viewer = isViewer(role);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const {
    adminUsers,
    isLoading,
    isSaving,
    error,
    successMessage,
    handleInviteUser,
    handleSaveUser,
    handleRevokeAccess,
  } = useAdminManagement();

  // Surface the hook's success/error messages as toasts instead of inline text.
  useEffect(() => {
    if (successMessage) {
      setSnackbar({ open: true, message: successMessage, severity: "success" });
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: "error" });
    }
  }, [error]);

  const handleCloseSnackbar = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleSaveUserWrapper = async (updatedUser) => {
    const success = await handleSaveUser(updatedUser);
    if (success) {
      setShowEditModal(false);
      setSelectedUser(null);
    }

    return success;
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowRevokeModal(true);
  };

  const handleRevokeAccessWrapper = async (user) => {
    const success = await handleRevokeAccess(user);
    if (success) {
      setShowRevokeModal(false);
      setSelectedUser(null);
    }

    return success;
  };

  return (
    <div>
      <PageHeader
        title="Admin Management"
        subtitle="Manage admin users and permissions"
      >
        <button
          onClick={() => setShowInviteModal(true)}
          className="sm:ml-2 px-4 sm:px-6 py-2 sm:py-3 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold text-xs sm:text-sm flex items-center gap-2 shadow"
        >
          <img src={AdminIcon} alt="admin icon" className="" loading="eager" />
          INVITE ADMIN
        </button>
      </PageHeader>

      {isLoading && (
        <div className="flex justify-center py-10">
          <CircularProgress size={28} />
        </div>
      )}

      <AdminUsers
        users={adminUsers}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        isLoading={isLoading}
        isViewer={viewer}
      />

      {admin && <AccountSettings />}

      <InviteAdminModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInviteUser}
        isSubmitting={isSaving}
      />

      <EditAdminModal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUserWrapper}
        user={selectedUser}
        isSubmitting={isSaving}
      />

      <RevokeAdminModal
        show={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setSelectedUser(null);
        }}
        onRevoke={handleRevokeAccessWrapper}
        user={selectedUser}
        isSubmitting={isSaving}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminManagement;
